import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt, TrendingUp, FileText, ChevronDown } from 'lucide-react';
import { useBalance, useInvoices } from '../api/hooks';
import { Cell, Chip, EmptyState, PageHeader, Section, Skeleton } from '../components/ui';
import { useT, useLocale } from '../i18n';

const PREVIEW_COUNT = 3;

export function Billing() {
  const navigate = useNavigate();
  const t = useT();
  const locale = useLocale();
  const balance = useBalance();
  const invoices = useInvoices();
  const [showAll, setShowAll] = useState(false);

  const balanceInfo = extractBalance(balance.data?.data);
  const allItems = extractInvoices(invoices.data);
  const items = showAll ? allItems : allItems.slice(0, PREVIEW_COUNT);
  const hiddenCount = allItems.length - PREVIEW_COUNT;

  return (
    <>
      <PageHeader title={t('billing.title')} subtitle={t('billing.subtitle')} />

      <section style={{ padding: '14px 16px 0' }}>
        <div
          style={{
            position: 'relative',
            background:
              'linear-gradient(135deg, #078DEE 0%, #0466B5 55%, #034A85 100%)',
            borderRadius: 'var(--r-xl)',
            padding: '22px 22px 20px',
            color: '#fff',
            overflow: 'hidden',
            boxShadow:
              '0 0 0 0.5px rgba(7,141,238,0.10), 0 8px 28px -8px rgba(7,141,238,0.45)',
          }}
        >
          {/* Decorative orbit highlight */}
          <div
            style={{
              position: 'absolute',
              top: -60,
              right: -60,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 65%)',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: -80,
              left: -40,
              width: 240,
              height: 240,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 60%)',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              opacity: 0.92,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            <TrendingUp size={14} strokeWidth={2.4} />
            {t('billing.balanceLabel')}
          </div>
          <div
            className="num"
            style={{
              position: 'relative',
              marginTop: 14,
              fontSize: 44,
              fontWeight: 700,
              letterSpacing: '-0.045em',
              lineHeight: 1.05,
            }}
          >
            {balance.isLoading ? (
              <Skeleton width={180} height={40} />
            ) : (
              <>
                <span style={{ fontSize: 19, opacity: 0.78, marginRight: 6, fontWeight: 600 }}>
                  {balanceInfo.currency ?? '$'}
                </span>
                {balanceInfo.amountDisplay ?? t('common.dash')}
              </>
            )}
          </div>
          {balanceInfo.frozen != null && (
            <div
              style={{
                position: 'relative',
                marginTop: 8,
                fontSize: 12.5,
                opacity: 0.82,
                letterSpacing: '-0.005em',
              }}
            >
              {t('billing.frozen')} {balanceInfo.currency ?? '$'}
              {balanceInfo.frozenDisplay}
            </div>
          )}
        </div>
      </section>

      <Section title={t('billing.recentInvoices')}>
        {invoices.isLoading && (
          <div style={{ padding: 14, display: 'grid', gap: 8 }}>
            <Skeleton height={48} radius={12} />
            <Skeleton height={48} radius={12} />
          </div>
        )}
        {!invoices.isLoading && items.length === 0 && (
          <EmptyState
            icon={<FileText size={28} />}
            title={t('billing.empty.title')}
            description={t('billing.empty.desc')}
          />
        )}
        {items.map((inv, idx) => {
          const uuid = String(inv.uuid ?? '');
          const title = String(
            inv.trade_no ?? inv.title ?? `Invoice#${inv.id ?? uuid.slice(0, 6)}`,
          );
          const amount =
            inv.actual_amount != null
              ? Number(inv.actual_amount)
              : inv.amount != null
                ? Number(inv.amount)
                : null;
          const currency = String(inv.currency ?? 'USD').toUpperCase();
          const symbol = currency === 'USD' ? '$' : currency + ' ';
          const status = String(inv.status ?? '');
          const date = String(inv.date ?? '') || fmtTime(inv.created_at, locale);
          const category = Array.isArray(inv.category)
            ? String(inv.category[0] ?? '')
            : String(inv.category ?? '');
          const isTopup = category === 'topup';
          return (
            <Cell
              key={uuid || idx}
              last={idx === items.length - 1 && !(hiddenCount > 0 && !showAll)}
              onClick={uuid ? () => navigate(`/billing/invoices/${uuid}`) : undefined}
              leading={
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 12,
                    background: isTopup ? 'rgba(34,197,94,0.12)' : 'rgba(7,141,238,0.10)',
                    color: isTopup ? 'var(--success)' : 'var(--brand)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Receipt size={18} />
                </div>
              }
              title={title}
              subtitle={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {category && (
                    <Chip color={isTopup ? 'success' : 'brand'}>
                      {isTopup ? t('billing.topup') : t('billing.charge')}
                    </Chip>
                  )}
                  <span>{date}</span>
                </span>
              }
              trailing={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 4,
                  }}
                >
                  <span
                    className="num"
                    style={{ fontWeight: 700, color: 'var(--tg-text)', fontSize: 15 }}
                  >
                    {amount != null && !Number.isNaN(amount)
                      ? `${symbol}${amount.toFixed(2)}`
                      : t('common.dash')}
                  </span>
                  {status && (
                    <Chip color={mapInvoiceStatus(status)}>{invoiceStatusLabel(status, t)}</Chip>
                  )}
                </div>
              }
            />
          );
        })}
        {hiddenCount > 0 && !showAll && (
          <button
            onClick={() => setShowAll(true)}
            style={{
              width: '100%',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              color: 'var(--brand)',
              fontSize: 14,
              fontWeight: 600,
              background: 'transparent',
              transition: 'background var(--tr-fast)',
              borderTop: '0.5px solid var(--tg-separator)',
            }}
          >
            {t('billing.showAll', { n: allItems.length })}
            <ChevronDown size={16} />
          </button>
        )}
      </Section>

    </>
  );
}

function extractBalance(data: unknown): {
  amountDisplay?: string;
  currency?: string;
  frozen?: number;
  frozenDisplay?: string;
} {
  if (!data || typeof data !== 'object') return {};
  const obj = data as Record<string, unknown>;
  // Neburst real shape: { balance: { all: <num>, currency: 'usd', frozen: <num> }, withdrawable_balance: {...} }
  const bal = (obj.balance ?? obj) as Record<string, unknown>;
  const candidate =
    typeof bal.all === 'number'
      ? bal.all
      : typeof bal.amount === 'number'
        ? bal.amount
        : typeof bal.available === 'number'
          ? bal.available
          : null;
  const frozen =
    typeof bal.frozen === 'number'
      ? bal.frozen
      : typeof bal.frozen_amount === 'number'
        ? bal.frozen_amount
        : null;
  const currencyRaw =
    typeof bal.currency === 'string' && bal.currency.trim() ? bal.currency.toUpperCase() : 'USD';
  return {
    amountDisplay: candidate != null ? candidate.toFixed(2) : undefined,
    currency: currencyRaw === 'USD' ? '$' : currencyRaw + ' ',
    frozen: frozen ?? undefined,
    frozenDisplay: frozen != null ? frozen.toFixed(2) : undefined,
  };
}

function extractInvoices(resp: unknown): Array<Record<string, unknown>> {
  if (!resp || typeof resp !== 'object') return [];
  const r = resp as Record<string, unknown>;
  // Route returns { data: <neburst data> }; Neburst data may be array or {invoices: []}/{list: []}/{data: []}
  const inner = (r.data ?? r) as Record<string, unknown>;
  if (Array.isArray(inner)) return inner as Array<Record<string, unknown>>;
  const arr = inner.invoices ?? inner.list ?? inner.items ?? inner.data;
  return Array.isArray(arr) ? (arr as Array<Record<string, unknown>>) : [];
}

function fmtTime(v: unknown, locale: string): string {
  if (typeof v !== 'string') return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(locale === 'zh' ? 'zh-CN' : 'en-US');
}

function mapInvoiceStatus(s: string): 'success' | 'warning' | 'neutral' | 'danger' {
  const lo = s.toLowerCase();
  if (lo === 'paid' || lo.includes('success')) return 'success';
  if (lo === 'unpaid' || lo === 'pending' || lo.includes('partially')) return 'warning';
  if (lo === 'cancelled' || lo === 'failed' || lo === 'expired') return 'neutral';
  return 'neutral';
}

function invoiceStatusLabel(s: string, t: ReturnType<typeof useT>): string {
  const lo = s.toLowerCase();
  const keyMap: Record<string, string> = {
    paid: 'invoice.status.paid',
    unpaid: 'invoice.status.unpaid',
    pending: 'invoice.status.pending',
    'partially-paid': 'invoice.status.partiallyPaid',
    partially_paid: 'invoice.status.partiallyPaid',
    cancelled: 'invoice.status.cancelled',
    canceled: 'invoice.status.cancelled',
    failed: 'invoice.status.failed',
    expired: 'invoice.status.expired',
  };
  const k = keyMap[lo];
  return k ? t(k as Parameters<typeof t>[0]) : s;
}
